import { ExpandMoreRounded } from "@mui/icons-material";
import {
    AccordionDetails,
    AccordionSummary,
    Box,
    styled,
    Typography,
} from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import { chain, sumBy } from "lodash";
import { useTranslation } from "react-i18next";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import {
    AssessmentVertical,
    GetConsumptionChartDataQuery,
    PropertyOwnershipType,
} from "graphql-types/graphql";
import { DASH_SEPARATOR } from "utils";
import { assessmentVerticalToTitle } from "utils/assessment";
import { percentageCalc } from "utils/maths";

import { formatConsumptionValue } from "./consumption.helpers";

type Props = {
    data: GetConsumptionChartDataQuery;
    type: ChartDisplayType;
};

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
}));

type YearlyConsumptionData =
    GetConsumptionChartDataQuery["getConsumptionChartData"][number];

const TableHeader = ({
    type,
    data,
}: {
    type: ChartDisplayType;
    data: YearlyConsumptionData;
}) => {
    const consumption =
        type === ChartDisplayType.ENERGY ? data.energy : data.emission;

    const total = sumBy(consumption, (c) => c.emission / 1000);
    const label = formatConsumptionValue(total, type);

    return (
        <>
            <Typography fontWeight="bold">{data.year}</Typography>&nbsp;·&nbsp;
            {label}
        </>
    );
};

const VerticalBox = ({
    data,
    vertical,
    type,
}: {
    data: YearlyConsumptionData;
    vertical: AssessmentVertical;
    type: ChartDisplayType;
}) => {
    const { t } = useTranslation();
    const isEnergy = type === ChartDisplayType.ENERGY;
    const primary = isEnergy ? data.energy : data.emission;
    const secondary = isEnergy ? data.emission : data.energy;

    const verticalPrimary = chain(primary).filter(
        (c) => c.vertical === vertical
    );
    const verticalSecondary = chain(secondary).filter(
        (c) => c.vertical === vertical
    );

    const totalPrimary = verticalPrimary
        .sumBy((c) => (isEnergy ? c.emission : c.emission / 1000))
        .value();

    const totalSecondary = verticalSecondary
        .sumBy((c) => (!isEnergy ? c.emission : c.emission / 1000))
        .value();

    const ownership = verticalPrimary
        .groupBy((c) => c.ownershipType)
        .mapValues((group) => group.reduce((sum, c) => sum + c.emission, 0));

    const tenantValue = ownership.get(PropertyOwnershipType.TENANT).value();
    const landlordValue = ownership.get(PropertyOwnershipType.LANDLORD).value();

    const tenant = Math.round(
        percentageCalc(
            isEnergy ? tenantValue : tenantValue / 1000,
            totalPrimary
        )
    );

    const landlord = Math.round(
        percentageCalc(
            isEnergy ? landlordValue : landlordValue / 1000,
            totalPrimary
        )
    );

    return (
        <Box sx={{ flex: 1 }}>
            <Typography fontWeight="bold" mb={3}>
                {assessmentVerticalToTitle(vertical, t)}
            </Typography>
            <Typography>
                {formatConsumptionValue(
                    totalSecondary,
                    isEnergy
                        ? ChartDisplayType.ENERGY
                        : ChartDisplayType.EMISSIONS
                ) ?? DASH_SEPARATOR}
            </Typography>
            <Typography sx={{ opacity: 0.5 }}>
                {formatConsumptionValue(
                    totalPrimary,
                    !isEnergy
                        ? ChartDisplayType.ENERGY
                        : ChartDisplayType.EMISSIONS
                ) ?? DASH_SEPARATOR}
            </Typography>
            <Typography sx={{ opacity: 0.5, marginTop: 2 }}>
                {landlord || DASH_SEPARATOR}% {t("common.landlord")}&nbsp;
                {landlordValue &&
                    formatConsumptionValue(
                        isEnergy ? landlordValue : landlordValue / 1000,
                        type
                    )}
            </Typography>
            <Typography sx={{ opacity: 0.5 }}>
                {tenant || DASH_SEPARATOR}% {t("common.tenant")}&nbsp;
                {tenantValue &&
                    formatConsumptionValue(
                        isEnergy ? tenantValue : tenantValue / 1000,
                        type
                    )}
            </Typography>
        </Box>
    );
};

export function ConsumptionTable({ data, type }: Props) {
    const tableData = data.getConsumptionChartData.filter(
        (c) => c.energy.length || c.emission.length
    );

    return (
        <div>
            {tableData.map((data, index) => (
                <Accordion key={data.year} defaultExpanded={index === 0}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreRounded fontSize="small" />}
                    >
                        <TableHeader type={type} data={tableData[index]} />
                    </AccordionSummary>
                    <AccordionDetails
                        sx={{
                            borderTop: 1,
                            borderColor: "divider",
                            padding: 3,
                            display: "flex",
                            gap: 3,
                        }}
                    >
                        {[
                            AssessmentVertical.ELECTRICITY,
                            AssessmentVertical.HEATING,
                            AssessmentVertical.WATER,
                            AssessmentVertical.ENERGY,
                            AssessmentVertical.TRANSPORT,
                            AssessmentVertical.WASTE,
                            AssessmentVertical.COOLING,
                        ].map((vertical) => (
                            <VerticalBox
                                key={vertical}
                                data={data}
                                vertical={vertical}
                                type={type}
                            />
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}
        </div>
    );
}
